import type { StateMachine } from "./state-machine.utils.js";
import { InvalidStateTransitionError } from "../errors/domain.error.js";
import { FoundationErrorConstants } from "../constants/foundation-errors.constants.js";

export abstract class StatefulValue<TValue extends string> {
  protected abstract readonly value: TValue;
  private readonly stateMachine: StateMachine<TValue>;

  private static readonly registries = new Map<
    Function,
    Map<string, StatefulValue<string>>
  >();

  protected constructor(stateMachine: StateMachine<TValue>, value: TValue) {
    this.stateMachine = stateMachine;

    // Note: We use the 'value' parameter instead of this.getValue() because child class fields

    const ctor = this.constructor;
    if (!StatefulValue.registries.has(ctor)) {
      StatefulValue.registries.set(ctor, new Map());
    }
    // biome-ignore lint/style/noNonNullAssertion: eslint migration
    StatefulValue.registries.get(ctor)!.set(value, this);
  }

  protected static lookup<T extends StatefulValue<string>>(
    ctor: Function,
    value: string,
  ): T | undefined {
    const registry = StatefulValue.registries.get(ctor);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Heterogeneous registry stores the base StatefulValue; the concrete subtype T is fixed by the calling static and safe to recover here
    return registry?.get(value) as T | undefined;
  }

  protected static lookupOrThrow<T extends StatefulValue<string>>(
    ctor: Function,
    value: string,
    errorFactory: (value: string) => Error,
  ): T {
    const instance = StatefulValue.lookup<T>(ctor, value);
    if (instance === undefined) {
      throw errorFactory(value);
    }
    return instance;
  }

  canTransitionTo(target: StatefulValue<TValue>): boolean {
    return this.stateMachine.isValidTransition(this.value, target.getValue());
  }

  isTerminal(): boolean {
    return this.stateMachine.isTerminal(this.value);
  }

  getValidNextStatusValues(): readonly TValue[] {
    return this.stateMachine.getValidNextStatuses(this.value);
  }

  getValue(): TValue {
    return this.value;
  }

  toString(): string {
    return this.value;
  }

  equals(other: StatefulValue<TValue>): boolean {
    return this.value === other.getValue();
  }

  assertCanTransitionTo(
    target: StatefulValue<TValue>,
    errors?: {
      sameState?: () => Error;
      terminalState?: () => Error;
      invalidTransition?: (
        from: TValue,
        to: TValue,
        validNext: readonly TValue[],
      ) => Error;
    },
  ): void {
    if (this.equals(target)) {
      if (errors === undefined) {
        throw new InvalidStateTransitionError(
          FoundationErrorConstants.STATE_ENTITY_TYPE,
          this.value,
          this.value,
        );
      }
      if (errors.sameState !== undefined) {
        throw errors.sameState();
      }

      return;
    }

    if (this.isTerminal()) {
      const error =
        errors?.terminalState?.() ??
        new InvalidStateTransitionError(
          FoundationErrorConstants.STATE_ENTITY_TYPE,
          this.value,
          target.getValue(),
        );
      throw error;
    }

    if (!this.canTransitionTo(target)) {
      const validNext = this.getValidNextStatusValues();
      const error =
        errors?.invalidTransition?.(this.value, target.getValue(), validNext) ??
        new InvalidStateTransitionError(
          FoundationErrorConstants.STATE_ENTITY_TYPE,
          this.value,
          target.getValue(),
        );
      throw error;
    }
  }
}
